import React, { useEffect, useState, useRef } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { Grid, Checkbox, FormControlLabel, Typography, Box } from '@material-ui/core'
import { CoreButton } from 'src/components/core'
import { useSelector, useDispatch } from 'react-redux'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import {
  intercomAuthService,
  intercomArticlesService,
} from 'src/api/authentication'
import styles from './styles'

const IntercomEmbed = props => {
  const { classes, onEmbed } = props
  const dispatch = useDispatch()
  const cardCreationProgress = useSelector(
    state => state.course.cardCreationProgress,
  )
  const [quizStarted, setQuizStarted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [articles, setArticles] = useState([])
  const [needsAuth, setNeedsAuth] = useState(false)
  const [selectedArticle, setSelectedArticle] = useState('')
  const [addQuiz, setAddQuiz] = useState(false)
  const [questionCount, setQuestionCount] = useState(1)
  const [authInitiated, setAuthInitiated] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)
  const authWindowRef = useRef(null)

  const filteredArticles = articles.filter(a =>
    a.title.toLowerCase().includes(searchText.toLowerCase()),
  )

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchArticles = async () => {
    setLoading(true)
    try {
      const response = await intercomArticlesService()
      const data = response?.data || response || []
      setArticles(Array.isArray(data) ? data : [])
      setNeedsAuth(false)
      setAuthInitiated(false)
      // Close the auth tab if it's still open
      if (authWindowRef.current && !authWindowRef.current.closed) {
        authWindowRef.current.close()
      }
      authWindowRef.current = null
    } catch (error) {
      setNeedsAuth(true)
      setArticles([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchArticles()
  }, [])

  useEffect(() => {
    if (!authInitiated) return

    const onFocus = () => {
      fetchArticles()
    }

    const onMessage = (e) => {
      if (e.data === 'intercom-connected') {
        fetchArticles()
      }
    }

    window.addEventListener('focus', onFocus)
    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('message', onMessage)
    }
  }, [authInitiated])

  const handleConnect = async () => {
    try {
      const redirectUri = window.location.origin + '/intercom-integration'
      const response = await intercomAuthService(redirectUri)
      const authUrl = response?.data || response
      if (authUrl) {
        authWindowRef.current = window.open(authUrl, 'intercomAuth')
        setAuthInitiated(true)
      }
    } catch (error) {
      console.error('Intercom auth error:', error)
    }
  }

  const handleArticleSelect = article => {
    setSelectedArticle(String(article.id))
    setSearchText(article.title)
    setDropdownOpen(false)
  }

  const handleEmbed = () => {
    const article = articles.find(a => String(a.id) === selectedArticle)
    if (article) {
      const quizCount = addQuiz ? questionCount : 0
      if (quizCount > 0) {
        setQuizStarted(true)
      }
      onEmbed(String(article.id), quizCount)
    }
  }

  // Auto-close modal when quiz generation completes (true → false)
  const progressSeenRef = useRef(false)
  useEffect(() => {
    if (cardCreationProgress) {
      progressSeenRef.current = true
    }
    if (quizStarted && progressSeenRef.current && !cardCreationProgress) {
      dispatch(messageBoxHideAction())
    }
  }, [quizStarted, cardCreationProgress])

  if (quizStarted) {
    return (
      <Grid container className={classes.root}>
        <div className={classes.content}>
          <div className={classes.loadingContainer}>
            <img
              src="/assets/new_mascot_noline_1.gif"
              height={200}
              style={{ margin: '0 auto' }}
            />
            <Box m={0.5} />
            <Typography className={classes.loadingTitle}>
              Generating Your Quiz with AI…
            </Typography>
            <Typography className={classes.loadingSubtitle}>
              Sit tight! We're using AI magic to generate the best quiz for you.
              The window will close automatically when it's ready.
            </Typography>
          </div>
        </div>
      </Grid>
    )
  }

  return (
    <Grid container className={classes.root}>
      <div className={classes.content}>
        <div className={classes.logoDiv}>
          <img
            className={classes.logo}
            src="/assets/intercom-logo.svg"
            alt="Intercom"
          />
        </div>
        <div>
          <p className={classes.title}>Intercom</p>
        </div>
        <div className={classes.messageDiv}>
          <p className={classes.message}>
            Embed an Intercom article into your course.
          </p>
        </div>

        {loading ? (
          <div className={classes.connectSection}>
            <p className={classes.message}>Loading...</p>
          </div>
        ) : needsAuth ? (
          <div className={classes.connectSection}>
            {authInitiated ? (
              <>
                <p className={classes.message}>
                  Log in to Intercom in the opened tab, then come back here.
                </p>
                <button className={classes.connectButton} onClick={fetchArticles}>
                  I've connected
                </button>
              </>
            ) : (
              <button className={classes.connectButton} onClick={handleConnect}>
                Connect to Intercom
              </button>
            )}
          </div>
        ) : (
          <div className={classes.selectSection}>
            <label className={classes.selectLabel}>Select an article</label>
            <div className={classes.dropdownWrapper} ref={dropdownRef}>
              <input
                className={classes.searchInput}
                type="text"
                placeholder="Search articles..."
                value={searchText}
                onChange={e => {
                  setSearchText(e.target.value)
                  setDropdownOpen(true)
                  if (selectedArticle) {
                    setSelectedArticle('')
                  }
                }}
                onFocus={() => setDropdownOpen(true)}
              />
              {dropdownOpen && (
                <div className={classes.dropdownList}>
                  {filteredArticles.length > 0 ? (
                    filteredArticles.map(article => (
                      <div
                        key={article.id}
                        className={`${classes.dropdownItem} ${
                          String(article.id) === selectedArticle
                            ? classes.dropdownItemActive
                            : ''
                        }`}
                        onClick={() => handleArticleSelect(article)}
                      >
                        {article.title}
                      </div>
                    ))
                  ) : (
                    <div className={classes.dropdownEmpty}>No articles found</div>
                  )}
                </div>
              )}
            </div>

            <div className={classes.quizSection}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addQuiz}
                    onChange={e => setAddQuiz(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <span className={classes.quizLabel}>
                    Add{' '}
                    <span className={classes.quizCountWrapper}>
                      <button
                        className={classes.quizCountBtn}
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (!addQuiz) setAddQuiz(true)
                          setQuestionCount(prev => Math.max(1, prev - 1))
                        }}
                      >
                        -
                      </button>
                      <span className={classes.quizCountValue}>{questionCount}</span>
                      <button
                        className={classes.quizCountBtn}
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (!addQuiz) setAddQuiz(true)
                          setQuestionCount(prev => Math.min(15, prev + 1))
                        }}
                      >
                        +
                      </button>
                    </span>{' '}
                    quiz questions related with this article after this page.
                  </span>
                }
              />
            </div>

            <div className={classes.buttonDiv}>
              <CoreButton
                color="primary"
                size="small"
                onClick={handleEmbed}
                disabled={!selectedArticle}
              >
                Embed
              </CoreButton>
            </div>
          </div>
        )}
      </div>
    </Grid>
  )
}

export default withStyles(styles, { withTheme: true })(IntercomEmbed)
